import { gql, gqlFetch } from '@/service/gql'
import type { SanitizedGql } from '@/util/gqlUtil'
import type { ResultOf } from 'gql.tada'
import { trackFieldsFrag } from '@/track/trackFieldsFrag'
import { getAuth } from '@/auth/authRef'
import type { Maybe } from '@amazon/vinyl-util'
import { playlistFieldsFrag } from '@/playlistdetail/playlistFieldsFrag'
import { stationFieldsFrag } from '@/service/fragments/stationFieldsFrag'
import { albumFieldsFrag } from '@/album/albumFieldsFrag'
import { artistFieldsFrag } from '@/artist/artistFieldsFrag'
import { podcastEpisodeFieldsFrag } from '@/podcast/podcastEpisodeFieldsFrag'
import { podcastShowFieldsFrag } from '@/podcast/podcastShowFieldsFrag'
import { videoFieldsFrag } from '@/video/videoFieldsFrag'
import { audiobookFieldsFrag } from '@/audiobook/audiobookFieldsFrag'

// language=GraphQL
const browseMusicCollectionsQuery = gql(
    `
  query browseMusicCollections(
    $limit: Float!
    $itemLimit: Float!
    $type: String,
    $cursor: String,
    $targets: [String],
    $isAuthenticated: Boolean!
  ) {

    browse: recommendationCollection(limit: $limit, itemLimit: $itemLimit, type: $type, cursor: $cursor, targets: $targets) {
      id
      recommendationConnection {
        edges {
          node {
            _type
            id
            itemType
            name
            recommendedContent {
              edges {
                node {
                  __typename
                    ...albumFields
                    ...artistFields
                    ...playlistFields
                    ...podcastShowFields
                    ...podcastEpisodeFields
                    ...stationFields
                    ...trackFields
                    ...videoFields
                    ...audiobookFields
                }
              }
              pageInfo {
                hasNextPage
                token
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          token
        }
      }
    }
  }
  `,
    [
        albumFieldsFrag,
        artistFieldsFrag,
        playlistFieldsFrag,
        podcastShowFieldsFrag,
        podcastEpisodeFieldsFrag,
        stationFieldsFrag,
        trackFieldsFrag,
        videoFieldsFrag,
        audiobookFieldsFrag,
    ]
)

export type BrowseMusicCollectionsResult = SanitizedGql<
    ResultOf<typeof browseMusicCollectionsQuery>
>

export type BrowseMusicCollectionsOptions = {
    readonly limit?: Maybe<number>
    readonly itemLimit?: Maybe<number>
    readonly type: string
    readonly cursor?: Maybe<string>
    /**
     * Optional `targets` filter. Used by genre / category pages — the
     * `category` collection type expects the category id here so the
     * gateway returns recommendations scoped to that genre.
     */
    readonly targets?: Maybe<readonly string[]>
}

export async function browseMusicCollections(options: BrowseMusicCollectionsOptions) {
    return gqlFetch(
        browseMusicCollectionsQuery,
        {
            limit: options.limit ?? 5,
            itemLimit: options.itemLimit ?? 15,
            type: options.type,
            cursor: options.cursor ?? null,
            targets: options.targets ? [...options.targets] : null,
            isAuthenticated: getAuth().isSignedIn(),
        },
        {
            cache: true,
            isRetryableError: () => true, // MUPS times out frequently
        }
    )
}
